import { Checkbox, Grid, Typography } from "@mui/material";
import React, { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import CustomTextField from "../../CustomTextField";
import CustomDatePicker from "../../CustomDatePicker";
import CustomTimePicker from "../../CustomTimePicker";
import { yupResolver } from "@hookform/resolvers/yup";
import { useReservationSchema } from "../../../validations/dailyReservationScheme";
import RoundButton from "../../RoundButton";
import { Stack } from "@mui/system";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import { getCurrentTime, getMinTime } from "../../CustomTimePicker/utils";
import { MOMENT_CONSTANTS, getTimeZone } from "../../../utils/momentUtils";
import { useTranslation } from "react-i18next";

const DailyReservationForm = React.memo((props) => {
  const { reservationCheckoutData } = useSelector(
    (state) => state.reservationReducer,
  );

  const { dailyReservationScheme } = useReservationSchema();
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    setError,
  } = useForm({
    mode: "all",
    defaultValues: reservationCheckoutData,
    resolver: yupResolver(dailyReservationScheme),
  });

  const dateVal = useWatch({ control, name: "fromDate" });
  const timeVal = useWatch({ control, name: "time" });
  const [isStartFromNow, setStartFromNow] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    const minTime = getMinTime(dateVal, props?.timeZone);
    if (minTime && !timeVal) {
      setValue("time", minTime?.format(MOMENT_CONSTANTS.time["24Hours"]));
    }
  }, [dateVal, props?.timeZone, timeVal, setValue]);

  const handleStartFromNow = () => {
    setStartFromNow((prev) => !prev);
    const minTime___ = getCurrentTime(new Date(), props?.timeZone);
    if (minTime___) {
      setValue("time", minTime___?.format(MOMENT_CONSTANTS.time["24Hours"]));
      setValue(
        "fromDate",
        minTime___?.format(MOMENT_CONSTANTS.date.monthDayYear),
      );
    }
  };

  return (
    <form
      onSubmit={handleSubmit((data) => {
        props?.handleSubmit(data);
      })}
    >
      <Grid
        container
        rowSpacing={1}
        sx={{ mt: 2 }}
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
      >
        <Grid item xs={6} style={{ opacity: isStartFromNow ? 0.5 : 1 }}>
          <Controller
            name="fromDate"
            control={control}
            render={({ field }) => (
              <CustomDatePicker
                {...field}
                size="small"
                disabled={isStartFromNow}
                required
                minDays={0}
                maxDays={30}
                inputFormat={MOMENT_CONSTANTS.date.monthDayYear}
                outputFormat={MOMENT_CONSTANTS.date.monthDayYear}
                placeholder="Check In Date"
                timeZone={props?.timeZone}
                label={
                  t("pages.makeReservation.input01") +
                  ` (${getTimeZone(props?.timeZone)})`
                }
                name="fromDate"
                error={!!errors.fromDate}
                helperText={errors.fromDate?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={6} style={{ opacity: isStartFromNow ? 0.5 : 1 }}>
          <Controller
            name="time"
            control={control}
            render={({ field }) => (
              <CustomTimePicker
                {...field}
                size="small"
                required
                disabled={isStartFromNow}
                timeZone={props?.timeZone}
                label={`${t("pages.makeReservation.input02")} (${getTimeZone(
                  props?.timeZone,
                )})`}
                name="time"
                minDate={dateVal}
                views={["hours"]}
                inputFormat={MOMENT_CONSTANTS.time["12Hours"]}
                outputFormat={MOMENT_CONSTANTS.time["24Hours"]} // if this is changed, change `convert24HourTo12Hour`
                handleError={(error) => setError("misc", { message: error })}
                error={!!errors.time}
                helperText={errors.time?.message}
              />
            )}
          />
        </Grid>
        <Grid display={"flex"} alignItems={"center"} ml={{ md: 2 }} mt={2}>
          <Checkbox
            size="small"
            checked={isStartFromNow}
            onChange={() => {
              handleStartFromNow();
            }}
          />
          <Typography>Start from now</Typography>
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="numOfDays"
            control={control}
            render={({ field }) => (
              <CustomTextField
                {...field}
                size="small"
                required
                placeholder={t("pages.makeReservation.input03")}
                label={t("pages.makeReservation.input03")}
                name="numOfDays"
                type="number"
                error={!!errors.numOfDays}
                helperText={errors.numOfDays?.message}
              />
            )}
          />
        </Grid>
        <Controller name="misc" control={control} render={() => <></>} />
      </Grid>

      <Stack
        sx={{
          mt: 2,
          position: "absolute",
          bottom: 0,
          width: "90%",
          right: 0,
          left: 0,
          marginRight: "auto",
          marginLeft: "auto",
        }}
        spacing={2}
      >
        <RoundButton
          sx={{ mt: 5, mb: 3 }}
          style={{
            width: "100%",
          }}
          size="large"
          variant="contained"
          type="submit"
          color="primary"
          rounded
          disabled={!isValid || !!errors?.misc?.message} // Disable button if form is invalid
        >
          <Typography variant="buttonText" color="common.buttonText">
            {t("commonLabels.text17")}
          </Typography>
        </RoundButton>
      </Stack>
    </form>
  );
});
export default DailyReservationForm;
